import React from "react";
import SliderControl from "./components/slider";
import ColorSwatch from "./components/color_swatch";
import ExpandableIconButton from "./components/expandable_icon_button";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faChevronRight } from "@fortawesome/free-solid-svg-icons";
import { poolUIViewModel } from "./pool_ui_view_viewmodel";

import {
  ArrowIcon,
  ArrowRightIcon,
  BrightnessIcon,
  ColorPaletteIcon,
  RulerIcon,
  SettingSliderHorizontalIcon,
  StairsDescendIcon,
  StarIcon,
  WeatherNightClearIcon,
} from "./icons";
import Row from "./components/row";
import Switch from "./components/switch";
import Accordion from "./components/accordeon";
import IconButton from "./components/icon_button";
import OptionsSelector from "./components/option_selector";
import {
  Localization,
  coverTypeOptions,
  poolColors,
  poolCoverOptions,
  poolTypesOptions,
  spotOptions,
  stairsOptions,
  yesNoOptions,
} from "./pool_ui_props";
import SVGIcon from "./components/svg_icon";
import SceneManager from "../scene_mgr";

interface PoolUIViewProps {
  sceneManager: SceneManager; // Replace 'SceneManager' with the actual type of your sceneManager
}

const PoolUIView: React.FC<PoolUIViewProps> = ({ sceneManager }) => {
  
  const remoteData: Localization = (window as any).flexline;
  const localization = remoteData.configurator.i18n;

  const {
    isPanelVisible,
    togglePanelVisibility,
    length,
    width,
    depth,
    handleLengthChange,
    handleWidthChange,
    handleDepthChange,
    toggleDayNight,
    handlePoolTypeSelection,
    poolTypeName,
    poolType,
    numSpots,
    handleNumSpotsChange,
    handleStairType,
    stairType,
    selectedColor,
    handleColorSelection,
    handleEditorModeToggle,
    handleSidePanelToggle,
    isMobile,
    handlePoolCoverType,
    poolCoverType,
    handleCoverSystem,
    coverSystem,
    handleFactorySettingsSize,
    useBeach,
    handleUseBeach,
    floatingStairs,
    handleFloatingStairsToggle,
    handlePoolTypeSelectionSide,
  } = poolUIViewModel(poolColors[0]);

  const sliderConfigurations = [
    {
      label: localization.poolLength ?? "Longueurs:",
      id: "length",
      min: 4,
      max: 20,
      step: 0.5,
      value: length,
      unit: "m",
      onChange: handleLengthChange,
    },
    {
      label: localization.poolWidth ?? "Largeur:",
      id: "width",
      min: 2,
      max: 5,
      step: 0.5,
      value: width,
      unit: "m",
      onChange: handleWidthChange,
    },
    {
      label: localization.poolDepth ?? "Profondeur:",
      id: "depth",
      min: 1,
      max: 2.5,
      step: 0.1,
      value: depth,
      unit: "m",
      onChange: handleDepthChange,
    },
  ];

  const handlePriceClick = () => {
    const currentConfiguration = {
      length: length,
      width: width,
      depth: depth,
      poolTypeName: poolTypeName,
      numSpots: numSpots,
      stairType: stairType,
      colorName: selectedColor?.name || "Aucune",
      coverType: poolCoverType,
      coverSystem: coverSystem,
      floatingStairs: floatingStairs,
      useBeach: useBeach,
      finalShot: sceneManager.getFinalShot(),
    };

    console.log("currentConfiguration", currentConfiguration);

    remoteData.configurator.submitConfiguration(currentConfiguration);
  };

  return (
    <div className="flex flex-col bg-white grid-cols-3" id="right_menu ">
      <div
        className={`absolute top-0 pb-32 right-0 z-0 bg-white flex flex-col items-start h-screen overflow-y-auto transition-all duration-200 ease-in-out ${
          isPanelVisible
            ? window.innerWidth >= 640
              ? "w-[500px]"
              : "w-full"
            : "w-0"
        }`}
      >
        <div className="flex justify-between items-center w-full">
          <button
            className="bg-gray-100 text-gray-600 p-2 rounded-full w-10 h-10 ml-4 flex-shrink-0 flex-grow-0 mt-2"
            onClick={togglePanelVisibility}
          >
            <SVGIcon IconComponent={ArrowIcon} scale="scale-75" />
          </button>
          <Switch
            LeftIconComponent={BrightnessIcon}
            RightIconComponent={WeatherNightClearIcon}
            onToggle={toggleDayNight}
          />
        </div>
        <div className="p-4 text-2xl font-black text-sky-700">
          {localization.panelTitle ?? "Flexline Configurator"}
        </div>
        <Accordion
          title={localization.poolTypeLabel ?? "Modèle"}
          subtitle={poolTypeName}
          initialIsOpen={true}
        >
          <OptionsSelector
            selectedOption={poolType}
            handleOptionChange={handlePoolTypeSelectionSide}
            options={poolTypesOptions}
            title={localization.poolTypeLabel}
          />
        </Accordion>
        <Accordion
          title={localization.poolDimensions ?? "Dimensions"}
          initialIsOpen={true}
          subtitle={`${length}m x ${width}m x ${depth}m`}
        >
          {sliderConfigurations.map((config, index) => (
            <div key={index} className="flex justify-between items-center">
              <SliderControl
                label={config.label}
                id={config.id}
                min={config.min}
                max={config.max}
                step={config.step}
                value={config.value}
                onChange={config.onChange}
                onStartEditing={() => handleEditorModeToggle(true)}
                onEndEditing={() => handleEditorModeToggle(false)}
              />
              <div className="bg-gray-200 px-4 py-2 rounded text-xl font-bold text-gray-600 w-20 justify-center items-center">
                {config.value}
                {config.unit}
              </div>
            </div>
          ))}
        </Accordion>
        <Accordion
          title={localization.poolColor ?? "Couleur"}
          subtitle={selectedColor?.name ?? "Aucune"}
          initialIsOpen = {true}
          extraComponent={
            <div
              className="w-6 h-6 mr-2 rounded-full"
              style={{
                backgroundColor: `#${selectedColor?.displayColor.toString(16)}`,
              }}
            ></div>
          }
        >
          <div className="flex mt-2">
            {poolColors.map((color, index) => (
              <div className="pr-8">
                <div>
                <ColorSwatch
                  key={index}
                  color={color}
                  selectedColor={selectedColor}
                  onSelect={handleColorSelection}
                />
                
                </div>
              </div>
            ))}
          </div>
        </Accordion>
        <Accordion
          title={localization.options ?? "Options"}
          initialIsOpen={true}
          subtitle=""
        >
          <div>
            <OptionsSelector
              selectedOption={numSpots}
              handleOptionChange={handleNumSpotsChange}
              options={spotOptions}
              title={localization.spots ?? "Nombre de spots"}
            />

            <OptionsSelector
              selectedOption={useBeach}
              handleOptionChange={handleUseBeach}
              options={yesNoOptions}
              title={localization.systemPlage}
            />

            <OptionsSelector
              selectedOption={stairType}
              handleOptionChange={handleStairType}
              options={stairsOptions}
              title={localization.stairs ?? "Esxaliers"}
            />

            <OptionsSelector
              selectedOption={floatingStairs}
              handleOptionChange={handleFloatingStairsToggle}
              options={yesNoOptions}
              title={localization.floatingStairsLabel}
            />

            <OptionsSelector
              selectedOption={poolCoverType}
              handleOptionChange={handlePoolCoverType}
              options={poolCoverOptions}
              title={localization.poolCover ?? "Pool Cover"}
            />

            <div
              className={`${
                poolCoverType === "None"
                  ? "opacity-0 pointer-events-none h-0"
                  : "opacity-100"
              }`}
            >
              <OptionsSelector
                selectedOption={coverSystem}
                handleOptionChange={handleCoverSystem}
                options={coverTypeOptions}
                title={localization.coverSystem ?? "Cover System"}
              />
            </div>
          </div>
        </Accordion>

        <div className="mb-16"> </div>
      </div>

      <div
        className={`absolute  mt-4 bottom-0 right-0 z-20 transition-all duration-200 ease-in-out  ${
          !isPanelVisible
            ? "w-0 opacity-0 pointer-events-none"
            : isMobile
            ? " w-full opacity-100"
            : " w-[500px] opacity-100"
        }`}
      >
        <button
          onClick={handlePriceClick}
          className="flex justify-between w-full items-center  hover:bg-sky-100 text-sky-700 text-lg font-black px-6  py-4  bg-white"
        >
          {localization.submitLabel ?? "Recevez votre prix"}
          <SVGIcon IconComponent={ArrowRightIcon} scale="scale-100" />
        </button>
      </div>
      <div
        className={`absolute bottom-4 p-5 z-20 transition-all duration-200 ease-in-out right-2
              ${
                isPanelVisible ? "opacity-0 pointer-events-none" : "opacity-100"
              }`}
      >
        <IconButton
          bgColor="bg-white"
          textColor="text-sky-700"
          onClick={togglePanelVisibility}
          IconComponent={SettingSliderHorizontalIcon}
        ></IconButton>
      </div>

      <div
        className={`absolute top-4 left-4 p-5 z-0 flex flex-col justify-center h-screen ${
          isMobile ? "hidden" : "block"
        }`}
      >
        <ExpandableIconButton IconComponent={StarIcon}>
          <Row
            options={poolTypesOptions}
            onOptionSelected={handlePoolTypeSelection}
          />
        </ExpandableIconButton>

        <ExpandableIconButton IconComponent={RulerIcon}>
          <div className="flex items-center flex-shrink-0">
            <Row
              options={[
                { display: "7x3m", data: "7:3" },
                { display: "11x4m", data: "11:4" },
                { display: "13x3m", data: "13:3" },
                { display: "15x4m", data: "15:4" },
              ]}
              onOptionSelected={handleFactorySettingsSize}
            />
            <button
              className="text-xs py-1 focus:outline-none px-2"
              onClick={handleSidePanelToggle}
            >
              {localization.defineYours ?? "Définir la vôtre"}
            </button>
          </div>
        </ExpandableIconButton>

        <ExpandableIconButton IconComponent={ColorPaletteIcon}>
          {poolColors.map((color, index) => (
            <ColorSwatch
              key={index}
              color={color}
              selectedColor={selectedColor}
              onSelect={handleColorSelection}
            />
          ))}
        </ExpandableIconButton>

        <ExpandableIconButton IconComponent={StairsDescendIcon}>
          {<Row options={stairsOptions} onOptionSelected={handleStairType} />}
        </ExpandableIconButton>
      </div>
    </div>
  );
};

export default PoolUIView;
